import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, Megaphone, Users, User, Plus, Edit2 } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const KIND_META = {
  broadcast: { Icon: Megaphone, label: "Broadcast", color: "var(--blue-dark)" },
  committee: { Icon: Users,     label: "Committee", color: "var(--alert)" },
  direct:    { Icon: User,      label: "Direct",    color: "var(--slate)" },
};

function timeAgo(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60 * 1000) return "just now";
  if (ms < 60 * 60 * 1000) return `${Math.floor(ms / 60000)}m`;
  if (ms < 24 * 60 * 60 * 1000) return `${Math.floor(ms / 3600000)}h`;
  return new Date(iso).toLocaleDateString();
}

export default function Messages() {
  const { user } = useAuth();
  const schemeId = getActiveSchemeId();
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [members, setMembers] = useState([]);
  const [me, setMe] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const loadThreads = async () => {
    const [t, mem] = await Promise.all([
      api.get(`/schemes/${schemeId}/threads`),
      api.get(`/schemes/${schemeId}/members`),
    ]);
    setThreads(t.data);
    setMembers(mem.data);
    const own = mem.data.find((m) => m.user_id === user?.user_id);
    setMe(own || (user?.system_role === "platform_admin" ? { role: "chair" } : null));
    if (!active && t.data.length) setActive(t.data[0].thread_id);
  };

  useEffect(() => { loadThreads().catch((e) => toast.error(apiError(e))); /* eslint-disable-next-line */ }, []);

  const startDirect = async (other_user_id) => {
    try {
      const { data } = await api.post(`/schemes/${schemeId}/threads`, {
        kind: "direct", participant_user_ids: [other_user_id],
      });
      await loadThreads();
      setActive(data.thread_id);
      setNewOpen(false);
    } catch (e) { toast.error(apiError(e)); }
  };

  const grouped = {
    broadcast: threads.filter((t) => t.kind === "broadcast"),
    committee: threads.filter((t) => t.kind === "committee"),
    direct: threads.filter((t) => t.kind === "direct"),
  };

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 lg:px-6 py-5" data-testid="messages-page">
        <div className="flex items-baseline justify-between mb-4">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-1" style={{ color: "var(--blue-dark)" }}>
              <MessageSquare size={12}/> Messages
            </span>
            <h1 className="text-xl font-bold" style={{ color: "var(--navy)" }}>Scheme messages</h1>
          </div>
          <Dialog open={newOpen} onOpenChange={setNewOpen}>
            <DialogTrigger asChild>
              <Button size="sm" style={{ background: "var(--blue)", color: "white" }} data-testid="new-message-btn"><Plus size={12} className="mr-1.5"/> New message</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Start a direct message</DialogTitle></DialogHeader>
              <NewDirectForm members={members} myId={user?.user_id} onSubmit={startDirect}/>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 rounded-2xl border bg-white overflow-hidden" style={{ borderColor: "var(--border)", minHeight: 600 }}>
          {/* Left rail */}
          <div className="lg:col-span-4 xl:col-span-3 border-r" style={{ borderColor: "var(--border)" }} data-testid="thread-rail">
            <div className="px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>Channels</div>
            </div>
            <ThreadGroup label="Broadcast" items={grouped.broadcast} active={active} onSelect={setActive}/>
            {grouped.committee.length > 0 && <ThreadGroup label="Committee" items={grouped.committee} active={active} onSelect={setActive}/>}
            <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>Direct messages</div>
            {grouped.direct.length === 0 ? (
              <p className="px-4 py-3 text-xs" style={{ color: "var(--tagline-grey)" }}>No direct threads yet.</p>
            ) : grouped.direct.map((t) => <ThreadRow key={t.thread_id} t={t} active={active === t.thread_id} onSelect={() => setActive(t.thread_id)} myId={user?.user_id}/>)}
          </div>

          {/* Right pane */}
          <div className="lg:col-span-8 xl:col-span-9 flex flex-col" style={{ minHeight: 600 }}>
            {active ? (
              <ThreadView
                key={active}
                schemeId={schemeId}
                threadId={active}
                myId={user?.user_id}
                me={me}
                onMessageSent={loadThreads}
              />
            ) : (
              <div className="flex-1 flex items-center justify-center text-sm" style={{ color: "var(--tagline-grey)" }}>
                Pick a channel to start reading.
              </div>
            )}
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function ThreadGroup({ label, items, active, onSelect }) {
  return (
    <>
      <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-wider" style={{ color: "var(--tagline-grey)" }}>{label}</div>
      {items.map((t) => <ThreadRow key={t.thread_id} t={t} active={active === t.thread_id} onSelect={() => onSelect(t.thread_id)}/>)}
    </>
  );
}

function ThreadRow({ t, active, onSelect }) {
  const Meta = KIND_META[t.kind] || KIND_META.direct;
  return (
    <button
      onClick={onSelect}
      data-testid={`thread-row-${t.thread_id}`}
      className="w-full px-4 py-2.5 flex items-center gap-3 text-left transition-colors border-l-2"
      style={{
        borderLeftColor: active ? "var(--blue)" : "transparent",
        background: active ? "var(--blue-light)" : "transparent",
      }}>
      <Meta.Icon size={14} style={{ color: Meta.color, flexShrink: 0 }}/>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate" style={{ color: "var(--navy)" }}>{t.title}</div>
        {t.last_message_preview && (
          <div className="text-xs truncate mt-0.5" style={{ color: "var(--tagline-grey)" }}>{t.last_message_preview}</div>
        )}
      </div>
      {t.unread_count > 0 && (
        <span className="text-[10px] font-bold tabular-nums px-1.5 py-0.5 rounded-full text-white" style={{ background: "var(--blue)", minWidth: 18, textAlign: "center" }} data-testid={`thread-unread-${t.thread_id}`}>
          {t.unread_count}
        </span>
      )}
    </button>
  );
}

function ThreadView({ schemeId, threadId, myId, me, onMessageSent }) {
  const [data, setData] = useState({ thread: null, messages: [], has_more: false });
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(null); // {message_id, body}
  const bottomRef = useRef(null);

  const reload = async () => {
    const res = await api.get(`/schemes/${schemeId}/threads/${threadId}`);
    setData(res.data);
    // mark-read on the last message
    if (res.data.messages.length) {
      const last = res.data.messages[res.data.messages.length - 1];
      await api.post(`/schemes/${schemeId}/threads/${threadId}/mark-read`, { last_read_message_id: last.message_id }).catch(() => {});
    }
    setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 60);
  };

  useEffect(() => { reload().catch((e) => toast.error(apiError(e))); /* eslint-disable-next-line */ }, [threadId]);

  const send = async (e) => {
    e?.preventDefault();
    const text = body.trim();
    if (!text) return;
    setSending(true);
    try {
      await api.post(`/schemes/${schemeId}/threads/${threadId}/messages`, { body: text });
      setBody("");
      await reload();
      onMessageSent?.();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  const saveEdit = async () => {
    try {
      await api.patch(`/schemes/${schemeId}/threads/${threadId}/messages/${editing.message_id}`, { body: editing.body });
      setEditing(null);
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const Meta = KIND_META[data.thread?.kind || "direct"];

  return (
    <>
      <div className="px-5 py-3 border-b flex items-center gap-2" style={{ borderColor: "var(--border)" }} data-testid="thread-header">
        {data.thread && <Meta.Icon size={14} style={{ color: Meta.color }}/>}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-bold truncate" style={{ color: "var(--navy)" }}>{data.thread?.title}</div>
          <div className="text-[10px]" style={{ color: "var(--tagline-grey)" }}>
            {data.thread?.kind === "broadcast" ? "All scheme members can read this channel."
              : data.thread?.kind === "committee" ? "Private to chair and committee members."
              : `Private conversation with ${(data.thread?.participant_names || []).filter((n) => n).join(", ")}`}
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="messages-pane" style={{ background: "var(--canvas-tint)" }}>
        {data.messages.length === 0 ? (
          <p className="text-center text-xs" style={{ color: "var(--tagline-grey)" }}>No messages yet — be the first.</p>
        ) : data.messages.map((m) => {
          const isOwn = m.sender_user_id === myId;
          const isEditing = editing?.message_id === m.message_id;
          return (
            <div key={m.message_id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`}>
              <div className="max-w-[78%]">
                {!isOwn && <div className="text-[10px] font-semibold mb-1 ml-3" style={{ color: "var(--slate)" }}>{m.sender_name}</div>}
                {isEditing ? (
                  <div className="rounded-2xl p-2.5 border" style={{ background: "white", borderColor: "var(--blue)" }}>
                    <Textarea rows={3} value={editing.body} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="text-sm" data-testid="edit-textarea"/>
                    <div className="flex justify-end gap-2 mt-2">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                      <Button size="sm" onClick={saveEdit} style={{ background: "var(--blue)", color: "white" }} data-testid="edit-save-btn">Save</Button>
                    </div>
                  </div>
                ) : (
                  <div className={`rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap break-words ${isOwn ? "rounded-tr-sm" : "rounded-tl-sm"}`}
                    style={{
                      background: isOwn ? "var(--blue)" : "white",
                      color: isOwn ? "white" : "var(--navy)",
                      border: isOwn ? "none" : "1px solid var(--border)",
                    }}>
                    {m.body}
                  </div>
                )}
                <div className={`flex items-center gap-2 mt-1 px-2 text-[10px] ${isOwn ? "justify-end" : "justify-start"}`} style={{ color: "var(--tagline-grey)" }}>
                  <span>{timeAgo(m.created_at)}</span>
                  {m.edited_at && <span>(edited)</span>}
                  {isOwn && m.is_editable && !isEditing && (
                    <button onClick={() => setEditing({ message_id: m.message_id, body: m.body })} className="hover:underline" data-testid={`edit-btn-${m.message_id}`}>edit</button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef}/>
      </div>
      {/* Composer */}
      {data.thread && (
        <form onSubmit={send} className="border-t px-4 py-3 flex items-end gap-2" style={{ borderColor: "var(--border)" }} data-testid="composer">
          <Textarea
            rows={1}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(e); } }}
            placeholder="Type a message…"
            className="resize-none text-sm"
            data-testid="composer-input"
          />
          <Button type="submit" disabled={sending || !body.trim()} style={{ background: "var(--blue)", color: "white" }} data-testid="composer-send">
            <Send size={14}/>
          </Button>
        </form>
      )}
    </>
  );
}

function NewDirectForm({ members, myId, onSubmit }) {
  const [pick, setPick] = useState("");
  const others = members.filter((m) => m.user_id !== myId);
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (pick) onSubmit(pick); }} className="space-y-3 mt-2" data-testid="new-direct-form">
      <div>
        <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>Send to</Label>
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger className="mt-1" data-testid="new-direct-select"><SelectValue placeholder="Pick a member…"/></SelectTrigger>
          <SelectContent>
            {others.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name || m.email} ({m.role})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <DialogFooter>
        <Button type="submit" disabled={!pick} style={{ background: "var(--blue)", color: "white" }} data-testid="new-direct-submit">Start conversation</Button>
      </DialogFooter>
    </form>
  );
}
